import { Link, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Badge } from '@/shared/ui/badge'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import { t } from '@/shared/lib/i18n'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import type { SharedProps } from '@/shared/types/SharedProps'
import { useSiteDateTime } from '@/shared/hooks/useSiteDateTime'
import {
  accessAdminUserPath,
  adminUsersPath,
  editAdminUserPath,
  notificationPreferencesAdminUserPath,
  resendAccessAdminUserPath,
} from '@/shared/lib/routes'

const CUSTOMER_ROLES: readonly string[] = ['customer_admin', 'customer_supervisor', 'customer_user']

interface AssignmentItem {
  id: number
  name: string
}

interface UserShow {
  id: number
  // REBUILD-525: canonical "Last, First (ID:id)" from Web::Admin::UserShowSerializer#display_name.
  displayName: string
  firstName: string
  lastName: string
  email: string
  jobTitle: string | null
  employeeId: string | null
  role: string | null
  roleDisplayName: string | null
  active: boolean
  disabled: boolean
  lastLogonAt: string | null
  pendingEmailUnconfirmed: boolean
  pendingPasswordSetup: boolean
  sites: AssignmentItem[]
  departments: AssignmentItem[]
  units: AssignmentItem[]
}

interface Props {
  user: UserShow
  currentUserIsAdmin: boolean
  [key: string]: unknown
}

const UserProfilePage = () => {
  const { formatDateTime } = useSiteDateTime()
  const { user, currentUserIsAdmin } = usePage<Props & SharedProps>().props
  const canUpdateUser = useCan(Resources.ADMIN_USER, Actions.UPDATE)

  useBreadcrumbs([
    { label: t('nav.administration') },
    { label: t('nav.users'), href: adminUsersPath() },
    { label: user.displayName },
  ])

  return (
    <div className="space-y-6">
      {/* Profile header */}
      <div className="rounded-lg border p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold">{user.displayName}</h1>
            <p className="text-muted-foreground">{user.email}</p>
            <div className="mt-2 flex items-center gap-2">
              {user.roleDisplayName && <Badge variant="secondary">{user.roleDisplayName}</Badge>}
              {user.active ? (
                <Badge variant="outline" className="text-green-700 border-green-300">
                  {t('admin.users.statusActive')}
                </Badge>
              ) : (
                <Badge variant="outline" className="text-red-700 border-red-300">
                  {t('admin.users.statusInactive')}
                </Badge>
              )}
            </div>
          </div>
          {canUpdateUser && (
            <div className="flex gap-2">
              {currentUserIsAdmin && (
                <Button asChild variant="outline">
                  <Link href={resendAccessAdminUserPath(user.id)} method="post" as="button">
                    {user.pendingPasswordSetup
                      ? t('admin.users.resendAccessButton')
                      : t('admin.users.resendPasswordButton')}
                  </Link>
                </Button>
              )}
              <Button asChild variant="outline">
                <Link href={editAdminUserPath(user.id)}>{t('admin.users.editButton')}</Link>
              </Button>
              {currentUserIsAdmin && CUSTOMER_ROLES.includes(user.role ?? '') && (
                <Button asChild variant="outline">
                  <Link href={accessAdminUserPath(user.id)}>
                    {t('admin.users.manageAccessButton')}
                  </Link>
                </Button>
              )}
              {currentUserIsAdmin && (
                <Button asChild variant="outline">
                  <Link href={notificationPreferencesAdminUserPath(user.id)}>
                    {t('admin.users.notificationPreferencesButton')}
                  </Link>
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Left column: assignments */}
        <div className="space-y-4">
          <div className="rounded-lg border p-4">
            <h2 className="mb-3 font-semibold">{t('admin.users.show.sites')}</h2>
            {user.sites.length > 0 ? (
              <ul className="space-y-1">
                {user.sites.map((site) => (
                  <li key={site.id} className="text-sm">
                    {site.name}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">—</p>
            )}
          </div>

          <div className="rounded-lg border p-4">
            <h2 className="mb-3 font-semibold">{t('admin.users.show.departments')}</h2>
            {user.departments.length > 0 ? (
              <ul className="space-y-1">
                {user.departments.map((department) => (
                  <li key={department.id} className="text-sm">
                    {department.name}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">—</p>
            )}
          </div>

          <div className="rounded-lg border p-4">
            <h2 className="mb-3 font-semibold">{t('admin.users.show.units')}</h2>
            {user.units.length > 0 ? (
              <ul className="space-y-1">
                {user.units.map((unit) => (
                  <li key={unit.id} className="text-sm">
                    {unit.name}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">—</p>
            )}
          </div>
        </div>

        {/* Right column: account details */}
        <div className="rounded-lg border p-4">
          <h2 className="mb-4 font-semibold">{t('admin.users.show.profile')}</h2>
          <dl className="space-y-3">
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                {t('admin.users.table.jobTitle')}
              </dt>
              <dd className="text-sm">{user.jobTitle ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                {t('admin.users.table.employeeId')}
              </dt>
              <dd className="text-sm">{user.employeeId ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted-foreground">
                {t('admin.users.table.lastLogon')}
              </dt>
              <dd className="text-sm">
                {user.lastLogonAt
                  ? formatDateTime(user.lastLogonAt)
                  : t('admin.users.show.neverLoggedIn')}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  )
}

export default UserProfilePage
